'use client'

import { useModal } from '@gyym/ui'
import React from 'react'

import { useAuthUser } from '@/modules/Auth/hooks/useAuthUser'
import { PasskeyPromotionModal } from '@/modules/Passkey/components/PasskeyPromotionModal'
import {
  consumePasskeyPromotionPending,
  isPasskeySupported,
} from '@/modules/Passkey/utils'

/**
 * ログイン直後のパスキー登録訴求を表示する
 *
 * ログイン成功時に sessionStorage に立てたフラグを、遷移先ページで検知して
 * ダイアログを表示する。表示条件:
 * - パスキー対応環境
 * - パスキー未登録
 * - 「今後この案内を非表示にする」が未設定
 *
 * メール認証（会員登録完了）のクエリがある場合は useVerifyEmailEffect 側で
 * 登録完了ダイアログとして表示するため、ここでは何もしない。
 */
export const PasskeyPromotionWatcher = () => {
  const { authUser, isLoggedIn } = useAuthUser()
  const { openModal } = useModal()
  const hasOpenedRef = React.useRef(false)

  React.useEffect(() => {
    if (hasOpenedRef.current) return
    if (!authUser || !isLoggedIn) return
    if (!isPasskeySupported()) return

    // 訴求フラグを立てる起点であるログインページでは、ログイン成功直後に
    // authUser が確定して本 Watcher が遷移前にフラグを消費してしまうため何もしない。
    // フラグは遷移先ページ（Pages Router）の Watcher で消費・表示させる。
    if (window.location.pathname.startsWith('/login')) return

    // メール認証（会員登録完了）フローは useVerifyEmailEffect 側で訴求する
    const searchParams = new URLSearchParams(window.location.search)
    if (
      searchParams.get('key') &&
      searchParams.get('expires') &&
      searchParams.get('signature')
    ) {
      return
    }

    if (authUser.hasPasskey || authUser.isPasskeyPromotionHidden) {
      // 表示対象外でもフラグは消費してログインセッション内で再判定しない
      consumePasskeyPromotionPending()
      return
    }

    if (!consumePasskeyPromotionPending()) return

    hasOpenedRef.current = true
    openModal(PasskeyPromotionModal, { context: 'login' })
  }, [authUser, isLoggedIn, openModal])

  return null
}
